/**
 * Safe Supabase Client
 * 
 * Wraps the Supabase client with null checks and graceful fallbacks
 * to prevent runtime errors when Supabase is not configured.
 */

import { supabase, hasSupabaseConfig } from './supabase';
import { notConfiguredError, ServiceError } from './service-errors';

/**
 * Check if Supabase is available
 */
export const isSupabaseAvailable = (): boolean => {
  return hasSupabaseConfig && supabase !== null;
};

/**
 * Get Supabase client with null check
 * @throws ServiceError if not configured
 */
export const getSupabase = () => {
  if (!isSupabaseAvailable()) {
    throw notConfiguredError('Supabase');
  }
  return supabase!;
};

/**
 * Safe wrapper for Supabase operations that returns null when not configured
 */
export const safeSupabaseQuery = async <T>(
  operation: () => Promise<{ data: T | null; error: any }>
): Promise<T | null> => {
  if (!isSupabaseAvailable()) {
    console.warn('Supabase not configured - returning null');
    return null;
  }

  try {
    const { data, error } = await operation();
    if (error) {
      console.error('Supabase query error:', error);
      throw error;
    }
    return data;
  } catch (error) {
    console.error('Supabase operation failed:', error);
    throw error;
  }
};

/**
 * Safe auth operations
 */
export const safeAuth = {
  /**
   * Get current session safely
   */
  async getSession() {
    if (!isSupabaseAvailable()) {
      return { session: null, error: notConfiguredError('Supabase Auth') };
    }
    
    try {
      const { data: { session }, error } = await supabase!.auth.getSession();
      return { session, error: error ? new ServiceError(error.message, 'UNAUTHORIZED') : null };
    } catch (error) {
      return { session: null, error: error instanceof ServiceError ? error : new ServiceError('Auth error', 'UNAUTHORIZED') };
    }
  },

  /**
   * Get current user safely
   */
  async getUser() {
    if (!isSupabaseAvailable()) {
      return { user: null, error: notConfiguredError('Supabase Auth') };
    }

    try {
      const { data: { user }, error } = await supabase!.auth.getUser();
      return { user, error: error ? new ServiceError(error.message, 'UNAUTHORIZED') : null };
    } catch (error) {
      return { user: null, error: error instanceof ServiceError ? error : new ServiceError('Auth error', 'UNAUTHORIZED') };
    }
  },

  /**
   * Sign in with email and password
   */
  async signInWithPassword(email: string, password: string) {
    if (!isSupabaseAvailable()) {
      throw notConfiguredError('Supabase Auth');
    }

    const { data, error } = await supabase!.auth.signInWithPassword({ email, password });
    if (error) {
      throw new ServiceError(error.message, 'UNAUTHORIZED');
    }
    return data;
  },

  /**
   * Sign out
   */
  async signOut() {
    if (!isSupabaseAvailable()) {
      return; // Already "signed out" if not configured
    }

    await supabase!.auth.signOut();
  },

  /**
   * Subscribe to auth state changes
   */
  onAuthStateChange(callback: (event: string, session: any) => void) {
    if (!isSupabaseAvailable()) {
      // Return a no-op unsubscribe function
      return { data: { subscription: { unsubscribe: () => {} } } };
    }

    return supabase!.auth.onAuthStateChange(callback);
  },
};

/**
 * Safe storage operations
 */
export const safeStorage = {
  /**
   * Get storage bucket safely
   */
  from(bucket: string) {
    if (!isSupabaseAvailable()) {
      // Return mock storage operations that fail gracefully
      return {
        upload: async () => ({ data: null, error: notConfiguredError('Supabase Storage') }),
        getPublicUrl: () => ({ data: { publicUrl: '' } }),
        download: async () => ({ data: null, error: notConfiguredError('Supabase Storage') }),
        remove: async () => ({ data: null, error: notConfiguredError('Supabase Storage') }),
        list: async () => ({ data: null, error: notConfiguredError('Supabase Storage') }),
      };
    }

    return supabase!.storage.from(bucket);
  },
};

/**
 * Create a safe query builder
 */
export const safeFrom = <T = any>(table: string) => {
  if (!isSupabaseAvailable()) {
    // Return a mock query builder that returns empty results
    const createMockBuilder = (): any => {
      const mockBuilder = {
        select: () => createMockBuilder(),
        insert: () => createMockBuilder(),
        update: () => createMockBuilder(),
        delete: () => createMockBuilder(),
        eq: () => createMockBuilder(),
        neq: () => createMockBuilder(),
        gt: () => createMockBuilder(),
        gte: () => createMockBuilder(),
        lt: () => createMockBuilder(),
        lte: () => createMockBuilder(),
        like: () => createMockBuilder(),
        ilike: () => createMockBuilder(),
        is: () => createMockBuilder(),
        in: () => createMockBuilder(),
        contains: () => createMockBuilder(),
        containedBy: () => createMockBuilder(),
        overlaps: () => createMockBuilder(),
        textSearch: () => createMockBuilder(),
        filter: () => createMockBuilder(),
        not: () => createMockBuilder(),
        or: () => createMockBuilder(),
        and: () => createMockBuilder(),
        order: () => createMockBuilder(),
        limit: () => createMockBuilder(),
        range: () => createMockBuilder(),
        single: () => Promise.resolve({ data: null, error: notConfiguredError('Supabase') }),
        maybeSingle: () => Promise.resolve({ data: null, error: null }),
        then: (resolve: any) => Promise.resolve({ data: [], error: null }).then(resolve),
        catch: (reject: any) => Promise.resolve({ data: [], error: null }).catch(reject),
      };
      return mockBuilder;
    };
    return createMockBuilder();
  }

  return supabase!.from(table);
};

export default {
  isAvailable: isSupabaseAvailable,
  getClient: getSupabase,
  safeQuery: safeSupabaseQuery,
  auth: safeAuth,
  storage: safeStorage,
  from: safeFrom,
};


